import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, type Locale } from "@tovi/shared";
import { Button, LocaleSwitch, PasswordInput, resolveErrorMessage, useTranslate } from "@tovi/ui";
import { authClient } from "@/lib/auth-client.ts";
import { passkeySupported, signInWithPasskey as passkeySignIn } from "@/lib/passkey.ts";

/**
 * Signing an employee in.
 *
 * Username and password is the fallback; a passkey is the credential this
 * screen would rather use, offered wherever the native passkey plugin
 * reports a platform authenticator (`#268`).
 * Either way the result is a **bearer token** — `apps/staff/src/lib/api.ts`
 * is the only thing that reads it, on the same terms as `apps/pos`.
 */
export function SignInScreen({
  locale,
  onLocaleChange,
  onSignedIn,
  continuingApproval = false,
}: {
  locale: Locale;
  onLocaleChange: (next: Locale) => void;
  onSignedIn: () => void;
  /** A `pendingCode` is waiting in the URL (`main.tsx`) — signing in continues to it. */
  continuingApproval?: boolean;
}) {
  const t = useTranslate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const passkeyAvailable = useQuery({
    queryKey: ["passkey-available"],
    queryFn: passkeySupported,
    staleTime: Infinity,
  });

  async function signInWithPassword() {
    setBusy(true);
    setError(null);
    try {
      const result = await authClient.signIn.username({ username, password });
      if (result.error) throw new ApiError("UNAUTHENTICATED", t("staff.signIn.rejected"));
      onSignedIn();
    } catch (caught) {
      setError(resolveErrorMessage(caught, t, "staff.signIn.unreachable"));
    } finally {
      setBusy(false);
    }
  }

  async function signInWithPasskey() {
    setBusy(true);
    setError(null);
    try {
      const result = await passkeySignIn();
      if (result === "failed") {
        throw new ApiError("UNAUTHENTICATED", t("staff.signIn.passkeyRejected"));
      }
      // A person backing out of their own device's prompt is not a failure
      // worth a red banner — there is nothing here to explain that the
      // system, rather than the person, got wrong.
      if (result === "signed-in") onSignedIn();
    } catch (caught) {
      setError(resolveErrorMessage(caught, t, "staff.signIn.unreachable"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="flex w-full max-w-sm flex-col gap-4">
        <div className="flex justify-end">
          <LocaleSwitch current={locale} onSelect={onLocaleChange} label={t("shell.language")} />
        </div>

        <h1 className="text-xl font-semibold text-ink-body">{t("staff.signIn.title")}</h1>
        <p className="text-sm text-ink-muted">{t("staff.signIn.subtitle")}</p>
        {continuingApproval ? (
          <p className="text-sm text-ink-muted">{t("staff.approve.continueAfterSignIn")}</p>
        ) : null}

        {passkeyAvailable.data ? (
          <>
            <Button onClick={signInWithPasskey} disabled={busy}>
              {t("staff.signIn.passkey")}
            </Button>
            <div className="flex items-center gap-3 text-xs text-ink-subtle">
              <span className="h-px flex-1 bg-line" />
              {t("staff.signIn.or")}
              <span className="h-px flex-1 bg-line" />
            </div>
          </>
        ) : null}

        <input
          className="h-12 rounded-control border border-line bg-surface px-3 text-ink-body"
          placeholder={t("staff.signIn.username")}
          autoComplete="username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
        />
        <PasswordInput
          size="lg"
          placeholder={t("staff.signIn.password")}
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          label={t("auth.showPassword")}
        />

        {error ? <p className="text-sm text-danger">{error}</p> : null}

        <Button
          variant="secondary"
          onClick={signInWithPassword}
          disabled={busy || !username || !password}
        >
          {t("staff.signIn.connect")}
        </Button>
      </div>
    </div>
  );
}
